/**
 * SC-255 — how far the browser is zoomed in, derived without a baseline.
 *
 * No engine exposes an absolute page-zoom factor, and Chrome/Safari persist zoom per origin
 * across sessions. So any "drift from the value captured at load" measurement reports 100% for a
 * user who reopens Sevv already zoomed — very likely the most common shape of the complaint.
 * Everything here is therefore computed from the values passed in at the moment of the call, and
 * these functions stay pure so they can be unit-tested (trackpad gestures cannot be synthesised).
 */

/**
 * Minimum zoom that earns the user a warning.
 *
 * Deliberately well above 100%: plenty of people run at 110-125% permanently (large monitors,
 * eyesight, accessibility) with nothing clipped, and warning them every session about a
 * deliberate choice is worse than staying quiet. Tune here, not at the call sites.
 */
export const ZOOM_INDICATOR_MIN_FACTOR = 1.5

/** `visualViewport.scale` carries float noise around 1.0 even at rest. */
const SCALE_EPSILON = 0.01

/**
 * `outerWidth / innerWidth` is never exactly 1 — window chrome, scrollbars and browser sidebars
 * all shrink the content area without any zoom being applied.
 */
const RATIO_EPSILON = 0.05

export interface ZoomVisualViewportLike {
  scale: number
}

export interface ZoomWindowLike {
  innerWidth: number
  outerWidth: number
}

/**
 * Best available estimate of the current zoom, as a factor (1 = not zoomed in).
 *
 * Two signals, because neither covers both zoom kinds: `visualViewport.scale` is exact but only
 * moves for visual-viewport magnification (WebKit pinch), while page zoom (Blink pinch, macOS
 * smart zoom, Cmd+, and zoom persisted from a previous session) leaves it at 1.0 and only shows
 * up in the width ratio. We take the larger of the two rather than preferring one: it is
 * order-independent, and when both are in play it understates rather than inventing zoom.
 *
 * `devicePixelRatio` is deliberately unused. It is `displayScale x zoom` with the two
 * inseparable, and it also changes when a window is dragged to a monitor with a different scale
 * factor — a false positive on the multi-monitor setups most of our users have.
 *
 * Zoom *out* is not reported: it shrinks the UI, so controls stay reachable.
 */
export function estimateZoomFactor(
  vv: ZoomVisualViewportLike | undefined,
  win: ZoomWindowLike
): number {
  const fromScale = vv && vv.scale > 1 + SCALE_EPSILON ? vv.scale : 1

  const ratio =
    win.innerWidth > 0 && win.outerWidth > 0 ? win.outerWidth / win.innerWidth : 1
  const fromRatio = ratio > 1 + RATIO_EPSILON ? ratio : 1

  return Math.max(fromScale, fromRatio)
}

export function isZoomedPastThreshold(
  factor: number,
  min: number = ZOOM_INDICATOR_MIN_FACTOR
): boolean {
  return factor >= min
}
